import React, { useEffect, useMemo, useRef } from 'react';

import { useAccess } from '../auth/AccessContext.jsx';
import { ACTIVITY_EVENTS } from '../auth/idle.js';
import { pollGate } from '../businessDay/businessDay.js';
import { createLiveHub } from './hub.js';
import { LiveContext } from './LiveContext.js';

/**
 * Live screens (docs/live-sync/implementation-plan.md 4.6): the one hub every
 * screen subscribes to with useLiveTopic, for the life of the signed-in
 * Workspace.
 *
 * Mounted inside the AccessProvider (AuthGate), AROUND the BusinessDayProvider
 * and the NotificationProvider (Console.jsx): the business-day chip listens to
 * it, and the NotificationProvider - which owns the tab's one socket - feeds
 * it every message (hub.receive) and every (re)connect (hub.connected).
 *
 * What it adds to the hub:
 *
 *   - the activity gate: the time of the person's last activity, read through
 *     businessDay.js pollGate exactly as the business day's poll and the
 *     bell's socket read it - so live reloads stop when the poll would, and
 *     never keep an idle session signed in;
 *   - activity (and the page shown again) wakes the screens a hint marked
 *     stale while the person was away;
 *   - a session hint (this person suspended, reset, or their roles changed)
 *     re-reads the principal (GET /api/me) quietly (AuthGate.jsx
 *     refreshPrincipal - no page loader): a session the server has ended
 *     signs out with the reason, as it does for any API call; a change of
 *     roles re-draws the screens the person may now see.
 *
 * On sign-out the AccessProvider unmounts this, and the hub is stopped with it.
 */
const LiveProvider = ({ children }) => {
    const { api, refreshPrincipal } = useAccess();
    const lastActivity = useRef(Date.now());
    // AuthGate's quiet re-read of /api/me (no page loader, so no screen unmounts).
    const reloadRef = useRef(refreshPrincipal);
    reloadRef.current = refreshPrincipal;

    // One hub per signed-in api (a new sign-in is a new api, so a new hub).
    const hub = useMemo(() => createLiveHub({
        isActive: () => pollGate({ nowMs: Date.now(), lastActivityMs: lastActivity.current, hidden: document.hidden === true }).open,
    }), [api]);

    useEffect(() => {
        const onActivity = () => {
            lastActivity.current = Date.now();
            hub.activity();
        };
        // The page shown again counts as coming back: stale screens reload once.
        const onVisibility = () => {
            if (document.hidden !== true) onActivity();
        };
        ACTIVITY_EVENTS.forEach((name) => window.addEventListener(name, onActivity, { passive: true }));
        document.addEventListener('visibilitychange', onVisibility);
        // A session hint: re-read who this person is (see above).
        const offSession = hub.onSession(() => reloadRef.current?.());
        return () => {
            offSession();
            hub.stop();
            ACTIVITY_EVENTS.forEach((name) => window.removeEventListener(name, onActivity));
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [hub]);

    return <LiveContext.Provider value={hub}>{children}</LiveContext.Provider>;
};

export default LiveProvider;
